import { DEFAULT_STOCKPILE_TOOL_MODE, stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import { meshPickTargetsAtom, pointCloudTargetsAtom } from '@/atoms/viewer.atom';
import { sortVerticesByCentroidAngle } from '@/lib/stockpile/pointInPolygon';
import { Line } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as THREE from 'three';
import { pickSurface } from './pickSurface';

/**
 * In-viewport polygon picker for stockpile volumes.
 *
 * Mirrors `AreaMeasuringTool` and `AddSurfaceTool`'s mouse-pick patterns
 * (pickSurface for raycasting, NDC conversion, drag-vs-click thresholding,
 * close-loop hover detection). Differences:
 *
 *  - Vertices are stored in `stockpileToolModeAtom`, not local React state, so
 *    the side panel and saved-stockpile rendering can read them.
 *  - First pick locks `sourceLayerId`; subsequent picks that hit a different
 *    PCO are rejected with a toast.
 *  - On polygon close (right-click or near-first-vertex click), phase moves to
 *    `'closed'`. The `<StockpileComputePanel />` side panel watches for that
 *    transition and opens itself.
 *  - No drag handles after closing — editing existing stockpiles is a separate
 *    flow (re-pick from the saved list).
 */

const STOCKPILE_COLOR = '#fb923c'; // orange-400, distinct from add-surface blue
const CLOSE_THRESHOLD_PX = 20;

export const StockpileTool = () => {
  const [mode, setMode] = useAtom(stockpileToolModeAtom);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const camera = useThree((s) => s.camera);
  const renderer = useThree((s) => s.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const { t } = useTranslation('project');

  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const [isNearFirst, setIsNearFirst] = useState(false);

  const enabled = mode.enabled;
  const isPicking = enabled && mode.phase !== 'closed';

  const pickedVecs = useMemo(
    () => mode.pickedPoints.map((p) => new THREE.Vector3(p[0], p[1], p[2])),
    [mode.pickedPoints],
  );

  // Reset cursor when tool disabled.
  useEffect(() => {
    if (!enabled) {
      renderer.domElement.style.cursor = 'default';
      setHoverPoint(null);
      setIsNearFirst(false);
    }
  }, [enabled, renderer]);

  // Helper: screen distance from mouse to a world point (for close-loop hit test).
  const getScreenDistance = (clientX: number, clientY: number, worldPoint: THREE.Vector3) => {
    const rect = renderer.domElement.getBoundingClientRect();
    const projected = worldPoint.clone().project(camera);
    const sx = ((projected.x + 1) / 2) * rect.width;
    const sy = ((-projected.y + 1) / 2) * rect.height;
    return Math.sqrt((clientX - rect.left - sx) ** 2 + (clientY - rect.top - sy) ** 2);
  };

  // Resolve the layer ID for a PCO hit. Picks store the layer ID via
  // pco.userData.layerId (set in Pointcloud.tsx). Returns null if not set.
  const layerIdForHit = (hit: THREE.Vector3): string | null => {
    // pickSurface() doesn't return the source PCO, so we re-pick all PCOs and
    // find the one whose pick is closest to the resolved hit. Cheap because
    // pco.pick is sub-ms and most scenes have 1–2 PCOs.
    let bestId: string | null = null;
    let bestDist = Infinity;
    for (const pco of pcoTargets) {
      const r = pco.pick(renderer, camera, raycaster.ray);
      if (r?.position) {
        const d = r.position.distanceTo(hit);
        if (d < bestDist) {
          bestDist = d;
          bestId = (pco.userData?.layerId as string | undefined) ?? null;
        }
      }
    }
    return bestId;
  };

  // Hover preview during picking.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;

    const onMove = async (e: MouseEvent) => {
      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      if (hit) {
        setHoverPoint(hit.position.clone());
        dom.style.cursor = 'crosshair';
        if (mode.pickedPoints.length >= 3) {
          const dist = getScreenDistance(e.clientX, e.clientY, new THREE.Vector3(...mode.pickedPoints[0]));
          setIsNearFirst(dist < CLOSE_THRESHOLD_PX);
        } else {
          setIsNearFirst(false);
        }
      } else {
        setHoverPoint(null);
        setIsNearFirst(false);
        dom.style.cursor = 'default';
      }
    };

    dom.addEventListener('pointermove', onMove);
    return () => {
      dom.removeEventListener('pointermove', onMove);
      dom.style.cursor = 'default';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking, pcoTargets, meshTargets, camera, renderer, raycaster, mode.pickedPoints.length]);

  // Click-to-pick handler. Mirrors AreaMeasuringTool.tsx:180-245 (drag-vs-click
  // threshold, close-loop on near-first-point click).
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;
    const DRAG_THRESHOLD = 5;

    const onDown = (e: MouseEvent) => {
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
    };
    const onUp = async (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (!pointerDownPos.current) return;
      const dx = e.clientX - pointerDownPos.current.x;
      const dy = e.clientY - pointerDownPos.current.y;
      pointerDownPos.current = null;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) return;

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      if (!hit) return;

      // Close-loop check (≥3 vertices, near first).
      if (mode.pickedPoints.length >= 3) {
        const dist = getScreenDistance(e.clientX, e.clientY, new THREE.Vector3(...mode.pickedPoints[0]));
        if (dist < CLOSE_THRESHOLD_PX) {
          // Auto-sort vertices around their centroid so a random click order
          // still yields a simple (non-self-intersecting) ring. See
          // sortVerticesByCentroidAngle for the limitation around concave
          // shapes — fine for stockpiles, which are star-shaped from above.
          setMode({
            ...mode,
            phase: 'closed',
            pickedPoints: sortVerticesByCentroidAngle(mode.pickedPoints),
          });
          setHoverPoint(null);
          setIsNearFirst(false);
          return;
        }
      }

      // First pick locks sourceLayerId. We try to find which PCO produced the hit.
      let nextSourceLayerId = mode.sourceLayerId;
      if (mode.pickedPoints.length === 0) {
        const lid = layerIdForHit(hit.position);
        if (!lid) {
          toast.error(t('stockpile.noPointCloudHit'));
          return;
        }
        nextSourceLayerId = lid;
      } else if (mode.sourceLayerId) {
        // Reject if hit was clearly on a different PCO.
        const lid = layerIdForHit(hit.position);
        if (lid && lid !== mode.sourceLayerId) {
          toast.error(t('stockpile.crossLayerPickRejected'));
          return;
        }
      }

      const next = [...mode.pickedPoints, [hit.position.x, hit.position.y, hit.position.z]] as Array<
        [number, number, number]
      >;
      setMode({
        ...mode,
        phase: 'picking',
        pickedPoints: next,
        sourceLayerId: nextSourceLayerId,
      });
      setHoverPoint(null);
    };

    dom.addEventListener('pointerdown', onDown);
    dom.addEventListener('pointerup', onUp);
    return () => {
      dom.removeEventListener('pointerdown', onDown);
      dom.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking, mode, pcoTargets, meshTargets, camera, renderer, raycaster, t]);

  // Right-click during picking: if we have ≥3 vertices, close the polygon;
  // otherwise cancel. Matches the "right-click to finalize" plan.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;
    const onCtx = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (mode.pickedPoints.length >= 3) {
        // Auto-sort vertices around centroid (see close-loop branch for rationale).
        setMode({
          ...mode,
          phase: 'closed',
          pickedPoints: sortVerticesByCentroidAngle(mode.pickedPoints),
        });
        setHoverPoint(null);
        setIsNearFirst(false);
      } else {
        setMode({ ...DEFAULT_STOCKPILE_TOOL_MODE });
      }
    };
    dom.addEventListener('contextmenu', onCtx);
    return () => dom.removeEventListener('contextmenu', onCtx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking, mode]);

  // Escape during any tool phase cancels.
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMode({ ...DEFAULT_STOCKPILE_TOOL_MODE });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Render: vertex spheres, hover preview, polyline, optional close-indicator.
  // NOTE: hooks below MUST sit above the `if (!enabled) return null` guard so
  // the hook order stays stable across the enable/disable transition.
  const linePoints = useMemo(() => {
    const pts = [...pickedVecs];
    if (!isPicking || mode.phase === 'closed') {
      // Closed: explicit closing edge from last → first.
      if (pts.length >= 3) pts.push(pts[0]);
    } else if (hoverPoint && pickedVecs.length > 0) {
      // Picking: preview edge from last vertex to hover position.
      pts.push(isNearFirst && pts.length >= 3 ? pts[0] : hoverPoint);
    }
    return pts;
  }, [pickedVecs, hoverPoint, isPicking, mode.phase, isNearFirst]);

  // Origin for camera-relative rendering — anchors all child meshes near the
  // group origin so the modelView matrix Three.js sends to the GPU has small
  // translation values. Without this, every vertex sphere / line endpoint
  // sits at world coords ~10⁶ (UTM scale); the GPU's Float32 modelView matrix
  // loses ~1 m of precision and the markers visibly jitter as the camera
  // rotates. Same trick `<SavedAreaMeasurement>` uses (`fillOrigin`) and that
  // `<StockpileVisualization>` uses (`columnsGeometry.origin`).
  //
  // We anchor on the *first* picked vertex (stable across re-renders — only
  // changes on undo-back-to-empty). Before any pick, fall back to the hover
  // point — there's only one mesh on screen then so per-frame jitter of the
  // origin doesn't manifest as relative motion between markers.
  const origin = useMemo(() => {
    if (pickedVecs.length > 0) return pickedVecs[0];
    if (hoverPoint) return hoverPoint;
    return new THREE.Vector3();
  }, [pickedVecs, hoverPoint]);
  const offset = (v: THREE.Vector3): [number, number, number] => [v.x - origin.x, v.y - origin.y, v.z - origin.z];

  if (!enabled) return null;

  return (
    <group position={[origin.x, origin.y, origin.z]}>
      {/* Hover preview */}
      {isPicking && hoverPoint && (
        <mesh position={offset(hoverPoint)} renderOrder={Number.MAX_SAFE_INTEGER}>
          <sphereGeometry args={[0.18, 12, 12]} />
          <meshBasicMaterial color={STOCKPILE_COLOR} transparent opacity={0.45} depthTest={false} />
        </mesh>
      )}

      {/* Close indicator */}
      {isNearFirst && pickedVecs.length >= 3 && mode.phase !== 'closed' && (
        <mesh position={offset(pickedVecs[0])} renderOrder={Number.MAX_SAFE_INTEGER}>
          <sphereGeometry args={[0.35, 16, 16]} />
          <meshBasicMaterial color='lime' transparent opacity={0.6} depthTest={false} />
        </mesh>
      )}

      {/* Vertex markers */}
      {pickedVecs.map((p, i) => (
        <mesh key={i} position={offset(p)} renderOrder={Number.MAX_SAFE_INTEGER}>
          <sphereGeometry args={[0.18, 12, 12]} />
          <meshBasicMaterial color={STOCKPILE_COLOR} transparent opacity={0.95} depthTest={false} />
        </mesh>
      ))}

      {/* Polyline (picked + preview / closing edge). Drei <Line> takes a
          flat array of [x, y, z] tuples or Vector3s; we offset the points
          here too so they render in the same group-local frame. */}
      {linePoints.length >= 2 && (
        <Line
          points={linePoints.map((p) => offset(p))}
          color={STOCKPILE_COLOR}
          lineWidth={2.5}
          transparent
          opacity={mode.phase === 'closed' ? 0.95 : 0.75}
          depthTest={false}
        />
      )}

      {/* Faint preview of closing edge while picking (when cursor is far from first) */}
      {isPicking && hoverPoint && pickedVecs.length >= 2 && !isNearFirst && mode.phase !== 'closed' && (
        <Line
          points={[offset(hoverPoint), offset(pickedVecs[0])]}
          color={STOCKPILE_COLOR}
          lineWidth={1.5}
          transparent
          opacity={0.3}
          depthTest={false}
        />
      )}
    </group>
  );
};
